import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useBatchResolve } from "../../../../../queries/collaboration/tasks/use-batch-resolve";
import { getXtenantId, showToastOnApiCall } from "../../../../../utils/utils";

interface BatchDrawerProps {
  disclosureHost: DisclosureHost;
  setSelectedTaskModel?: (model: string[]) => void;
  title?: string;
}

type FormikValues = {
  resolvedDate: dayjs.Dayjs;
  note: string;
};

const validationSchema = Yup.object().shape({
  resolvedDate: Yup.date().required("Date is required"),
  note: Yup.string(),
});

const BatchResolveDrawer = ({
  disclosureHost,
  setSelectedTaskModel,
  title = "Resolve Task",
}: BatchDrawerProps) => {
  const { mutateAsync: batchResolved } = useBatchResolve();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const handleSubmit = async (values: FormikValues) => {
    showToastOnApiCall({
      apiCall: batchResolved({
        bulkResolved: {
          taskId: disclosureHost.data as string[],
          resolvedDate: values.resolvedDate?.toISOString() || "",
          description: values.note,
        },
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        setSelectedTaskModel?.([]);
        handleClose();
      },
    });
  };

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const formik = useFormik<FormikValues>({
    initialValues: {
      resolvedDate: dayjs(new Date()),
      note: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <BatchResolveForm
      formik={formik}
      disclosureHost={disclosureHost}
      title={title}
      handleClose={handleClose}
    />
  );
};

export default BatchResolveDrawer;

type BatchResolveFormProps = {
  formik: FormikProps<FormikValues>;
  disclosureHost: DisclosureHost;
  title: string;
  handleClose: () => void;
};

export const BatchResolveForm = ({
  formik,
  disclosureHost,
  title,
  handleClose,
}: BatchResolveFormProps) => {
  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={handleClose}
      title={title}
      w="44vw"
      footerActions={
        <>
          <Button
            type="button"
            variant="outlined"
            disabled={formik.isSubmitting}
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            onClick={formik.submitForm}
          >
            Mark as Resolved
          </Button>
        </>
      }
    >
      <Flex flexDirection={"column"} gap={3} width={"100%"}>
        <CustomDatePicker
          minDate={dayjs(new Date())}
          label="Date"
          value={formik.values.resolvedDate}
          onChange={(value) => formik.setFieldValue("resolvedDate", value)}
          required
        />
        <CustomTextArea
          label="Note"
          placeholder="Enter Note..."
          value={formik.values.note}
          onChange={(e) => formik.setFieldValue("note", e.target.value)}
          minRows={5}
        />
      </Flex>
    </SideSheetDrawer>
  );
};
